import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { FaTimes, FaMapMarkerAlt, FaExternalLinkAlt } from 'react-icons/fa';
import {
  TechSection, StatStrip, StatTile, StatLabel, StatValue, StatSub,
  TechChip, Empty, Loading, borderSoft, mono,
} from '../../styles/techComponents';
import { getBarrioDetalle } from '../../services/territorioService';

const CAT = {
  seguridad: { label: 'Seguridad', color: '#ef4444', emoji: '🚨' },
  obras: { label: 'Obras', color: '#3b82f6', emoji: '🚧' },
  salud: { label: 'Salud', color: '#22c55e', emoji: '➕' },
  servicios: { label: 'Servicios', color: '#06b6d4', emoji: '💧' },
  transito: { label: 'Tránsito', color: '#f59e0b', emoji: '🚗' },
  otro: { label: 'General', color: '#94a3b8', emoji: '📰' },
};
const ESTADO_COLOR = { detectado: '#f59e0b', tomado: '#00b4d8', en_gestion: '#8b5cf6', resuelto: '#22c55e', descartado: '#64748b' };
const fmtFecha = (f) => (f ? new Date(f).toLocaleDateString('es-AR') : 's/f');

export default function BarrioDetalle({ barrio, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null); setData(null);
    getBarrioDetalle(barrio.id)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(friendlyError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [barrio.id]);

  const cerrar = (
    <button type="button" onClick={onClose}
      style={{ background: 'none', border: `1px solid ${borderSoft}`, color: 'var(--text-secondary)', borderRadius: 6, padding: '0.3rem 0.6rem', cursor: 'pointer', fontFamily: mono, fontSize: '0.72rem' }}>
      <FaTimes /> Cerrar
    </button>
  );

  return (
    <TechSection title={`Inteligencia del barrio · ${barrio.nombre}`} icon={<FaMapMarkerAlt />} action={cerrar}>
      {loading && <Loading>Cruzando noticias, reportes y reclamos del barrio…</Loading>}
      {error && <div style={{ color: '#ef4444', fontSize: '0.85rem' }}>{error}</div>}
      {data && (
        <>
          <StatStrip>
            <StatTile><StatLabel>🚨 Seguridad</StatLabel><StatValue style={{ color: data.counts.robos ? '#ef4444' : undefined }}>{data.counts.robos}</StatValue><StatSub>noticias de inseguridad</StatSub></StatTile>
            <StatTile><StatLabel>📰 Noticias</StatLabel><StatValue>{data.counts.noticias}</StatValue><StatSub>que lo mencionan</StatSub></StatTile>
            <StatTile><StatLabel>🚧 Obras</StatLabel><StatValue>{data.counts.obras}</StatValue><StatSub>menciones</StatSub></StatTile>
            <StatTile><StatLabel>Reclamos</StatLabel><StatValue>{data.counts.reclamos}</StatValue><StatSub>{data.counts.reclamos_abiertos} abiertos</StatSub></StatTile>
            <StatTile><StatLabel>Reportes</StatLabel><StatValue>{data.counts.reportes}</StatValue><StatSub>que lo citan</StatSub></StatTile>
          </StatStrip>

          <div style={{ marginTop: '0.8rem', fontFamily: mono, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Noticias del barrio</div>
          {data.noticias.length === 0 && <Empty>Ningún medio monitoreado menciona este barrio todavía.</Empty>}
          {data.noticias.map((n, i) => {
            const c = CAT[n.categoria] || CAT.otro;
            return (
              <div key={i} style={{ padding: '0.5rem 0', borderBottom: `1px solid ${borderSoft}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span title={c.label} style={{ fontSize: '1rem' }}>{c.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={n.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text-primary)', fontSize: '0.88rem', textDecoration: 'none' }}>
                    {n.titulo} <FaExternalLinkAlt style={{ fontSize: '0.6rem', opacity: 0.5 }} />
                  </a>
                  <div style={{ fontFamily: mono, fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                    <TechChip style={{ color: c.color, borderColor: c.color }}>{c.label}</TechChip> {n.fuente || 'medio'} · {fmtFecha(n.fecha)}
                  </div>
                </div>
              </div>
            );
          })}

          {data.reportes.length > 0 && (
            <>
              <div style={{ marginTop: '0.9rem', fontFamily: mono, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Reportes que lo mencionan</div>
              {data.reportes.map((r, i) => (
                <div key={i} style={{ padding: '0.4rem 0', borderBottom: `1px solid ${borderSoft}`, fontSize: '0.85rem' }}>
                  <b>{r.titulo}</b> <TechChip>{r.categoria}</TechChip>
                  <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>{r.extracto}</div>
                </div>
              ))}
            </>
          )}

          {data.reclamos.length > 0 && (
            <>
              <div style={{ marginTop: '0.9rem', fontFamily: mono, fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Reclamos del barrio</div>
              {data.reclamos.map((r) => (
                <div key={r.id} style={{ padding: '0.35rem 0', borderBottom: `1px solid ${borderSoft}`, fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span>{r.titulo}</span>
                  <TechChip style={{ color: ESTADO_COLOR[r.estado], borderColor: ESTADO_COLOR[r.estado] }}>{String(r.estado).replace('_', ' ')}</TechChip>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </TechSection>
  );
}
